import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import type {
	UserInlinePreviewListRelay_knowledgeDiscovery$data,
	UserInlinePreviewListRelay_knowledgeDiscovery$key,
} from './__generated__/UserInlinePreviewListRelay_knowledgeDiscovery.graphql';
import { UserInlinePreview } from './UserInlinePreview';
import { UserInlinePreviewList } from './UserInlinePreviewList';

export type UserInlinePreviewListRelayProps = Omit<
	React.ComponentProps<typeof UserInlinePreviewList>,
	'children'
> & {
	knowledgeDiscoveryFragment?: UserInlinePreviewListRelay_knowledgeDiscovery$key;
	cloudId: string;
};

function normalizeUserSearchRows(
	data: UserInlinePreviewListRelay_knowledgeDiscovery$data | null | undefined,
) {
	if (
		!data ||
		!data.searchUsersV2 ||
		data.searchUsersV2.__typename !== 'KnowledgeDiscoverySearchUsers'
	) {
		return [];
	}
	const users =
		data.searchUsersV2.users?.filter((user): user is NonNullable<typeof user> => user != null) ??
		[];
	return users.map((user) => ({
		accountId: user.id,
		name: user.name,
		avatarUrl: user.avatarUrl ?? null,
		jobTitle: user.title ?? null,
		email: null,
		thirdPartyIntegrations: user.thirdPartyIntegrations?.map((integration) => ({
			externalId: integration.externalId,
			integrationId: integration.integrationId,
			product: integration.product,
		})),
	}));
}

/**
 * Relay-connected {@link UserInlinePreviewList} backed by Knowledge Discovery `userSearchV2`.
 * Spread `UserInlinePreviewListRelay_knowledgeDiscovery` on `knowledgeDiscovery` in the parent query
 * and pass fragment variables `accountIds`, `requestingUserId`, and `tenantId`.
 */
export function UserInlinePreviewListRelay({
	knowledgeDiscoveryFragment,
	cloudId,
	...listProps
}: UserInlinePreviewListRelayProps): React.JSX.Element {
	const userSearch = useFragment(
		graphql`
			fragment UserInlinePreviewListRelay_knowledgeDiscovery on KnowledgeDiscoveryQueryApi
			@argumentDefinitions(
				accountIds: { type: "[String!]!" }
				requestingUserId: { type: "String!" }
				tenantId: { type: "String!" }
			) {
				searchUsersV2(
					accountIds: $accountIds
					requestingUserId: $requestingUserId
					tenantId: $tenantId
				) @optIn(to: "KnowledgeDiscovery Search Users V2") {
					__typename
					... on KnowledgeDiscoverySearchUsers {
						users {
							id
							name
							avatarUrl
							title
							thirdPartyIntegrations {
								externalId
								integrationId
								product
							}
						}
					}
					... on QueryError {
						__typename
					}
				}
			}
		`,
		knowledgeDiscoveryFragment ?? null,
	);

	const rows = useMemo(() => normalizeUserSearchRows(userSearch), [userSearch]);

	return (
		<UserInlinePreviewList {...listProps}>
			{rows.map((row) => (
				<UserInlinePreview key={row.accountId} cloudId={cloudId} data={row} />
			))}
		</UserInlinePreviewList>
	);
}

export type { UserInlinePreviewListRelay_knowledgeDiscovery$key };
